import type {
  Action,
  AnyAction,
  Reducer,
  StoreEnhancer,
  StoreEnhancerStoreCreator,
} from 'redux';
import {BATCH} from 'redux-batched-actions';

import {
  recordRumDurationVital,
  recordRumError,
} from '../util/datadog/datadogRum';

/**
 * Records slow reduces to RUM, and the action that was mid-reduce when the
 * store first throws a Redux re-entrancy error (#3/#6/#9). The in-flight record
 * is cleared on return and on throw, so it only survives an uncatchable
 * termination that skipped Redux's own `finally`.
 */

export const SLOW_REDUCER_VITAL = 'redux_slow_reducer';
export const STUCK_STORE_MESSAGE = 'redux_store_stuck';
export const SLOW_REDUCER_THRESHOLD_MS = 1000;

const REDUX_REENTRANCY_ERROR =
  /Minified Redux error #(3|6|9)\b|while the reducer is executing/;

interface InFlightReduce {
  actionType: string;
  batchedActionTypes: string[] | undefined;
  startedAt: number;
}

let inFlight: InFlightReduce | undefined;
let reportedStuck = false;

function isBatchAction(action: AnyAction): action is Action<string> & {
  payload: AnyAction[];
} {
  return action.type === BATCH && Array.isArray(action.payload);
}

function describeAction(
  action: AnyAction
): Pick<InFlightReduce, 'actionType' | 'batchedActionTypes'> {
  return {
    actionType: String(action.type),
    batchedActionTypes: isBatchAction(action)
      ? action.payload.map(a => String(a.type))
      : undefined,
  };
}

export function withReducerTiming<S>(reducer: Reducer<S>): Reducer<S> {
  return (state, action) => {
    const startedAt = performance.now();
    inFlight = {...describeAction(action), startedAt};
    let next: S;
    try {
      next = reducer(state, action);
    } catch (err) {
      inFlight = undefined;
      throw err;
    }
    const durationMs = performance.now() - startedAt;
    inFlight = undefined;
    if (durationMs >= SLOW_REDUCER_THRESHOLD_MS) {
      recordRumDurationVital(SLOW_REDUCER_VITAL, {
        startTime: performance.timeOrigin + startedAt,
        duration: durationMs,
        context: describeAction(action),
      });
    }
    return next;
  };
}

function reportStuckStore(err: unknown, operation: string): void {
  if (reportedStuck || !(err instanceof Error)) {
    return;
  }
  if (!REDUX_REENTRANCY_ERROR.test(err.message)) {
    return;
  }
  reportedStuck = true;
  recordRumError(err, {
    message: STUCK_STORE_MESSAGE,
    operation,
    interruptedActionType: inFlight?.actionType,
    interruptedBatchedActionTypes: inFlight?.batchedActionTypes,
    interruptedMsAgo:
      inFlight == null
        ? undefined
        : Math.round(performance.now() - inFlight.startedAt),
  });
}

export const stuckStoreDiagnostics: StoreEnhancer =
  (createStore: StoreEnhancerStoreCreator) => (reducer, preloadedState) => {
    const store = createStore(reducer, preloadedState);
    return {
      ...store,
      getState: () => {
        try {
          return store.getState();
        } catch (err) {
          reportStuckStore(err, 'getState');
          throw err;
        }
      },
      dispatch: action => {
        try {
          return store.dispatch(action);
        } catch (err) {
          reportStuckStore(err, 'dispatch');
          throw err;
        }
      },
      subscribe: listener => {
        const unsubscribe = store.subscribe(listener);
        return () => {
          try {
            unsubscribe();
          } catch (err) {
            reportStuckStore(err, 'unsubscribe');
            throw err;
          }
        };
      },
    };
  };

export function resetStoreDiagnosticsForTests(): void {
  inFlight = undefined;
  reportedStuck = false;
}
